import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import TextCard from '~/components/Dynamic/TextCard';
import Image from '~/components/Dynamic/Image';
import Button from '~/components/Dynamic/Button';

export default function CustomersTestimonialSection({ blok }) {
  const testimonials = blok.testimonial;
  const [selectedTab, setSelectedTab] = useState(0);
  const tabSelectMotion = useMotionValue(selectedTab);
  const tabSelect = useSpring(selectedTab, { transition: 0.5 });
  const color = useTransform(tabSelectMotion, [0, 1, 2, 3], ['#FEE8B0', '#34D186', '#4DBC15', '#35B8B2']);
  const morph = useTransform(
    tabSelect,
    [0, 1, 2, 3],
    [
      'M217.912 6.87779C244.512 22.4035 260.458 68.8947 265.067 110.903C269.586 153.197 262.858 190.724 243.829 222.927C224.537 254.991 193.033 281.446 153.21 283.558C113.388 285.67 65.2462 263.44 34.6513 222.027C4.23296 180.542 -8.4621 119.801 8.03588 83.4359C24.6207 47.2834 70.7518 35.3609 112.314 21.3616C153.964 7.57446 191.222 -8.36265 217.912 6.87779Z',
      'M176.995 18.5C203.595 34.0257 260.531 37.9426 265.14 79.9514C269.659 122.245 259.526 158.872 240.498 191.075C221.205 223.139 193.107 250.493 153.284 252.606C113.461 254.718 67.5978 239.413 37.0029 198C6.58463 156.514 -9.49801 136.365 6.99997 100C23.5848 63.8475 47.9345 20.9993 89.4972 7C131.147 -6.78716 150.305 3.25955 176.995 18.5Z',
      'M172.5 47.5001C199.1 63.0258 243.545 50.4536 248.154 92.4623C252.673 134.756 245.945 172.283 226.916 204.486C207.624 236.549 176.121 263.004 136.298 265.117C96.4754 267.229 68.5959 232.413 38.001 191C7.58267 149.514 -8.99825 128.827 7.49973 92.4623C24.0846 56.3098 53.8393 16.9198 95.402 2.9205C137.052 -10.8667 145.81 32.2596 172.5 47.5001Z',
      'M214.997 4.4366C241.597 19.9623 257.543 66.4535 262.152 108.462C266.671 150.756 259.943 188.283 240.914 220.485C221.622 252.549 190.118 279.005 150.295 281.117C110.473 283.229 81.594 242.913 50.9991 201.5C20.5808 160.014 -11.377 117.359 5.12098 80.9947C21.7059 44.8422 71.9377 45.9993 113.5 32C155.15 18.2128 188.307 -10.8038 214.997 4.4366Z',
    ]
  );

  useEffect(() => {
    tabSelect.set(selectedTab);
    tabSelectMotion.set(selectedTab);
  }, [selectedTab]);

  return (
    <section className="py-20 text-white bg-black md:py-28">
      <div className="container">
        <div className="flex justify-center">
          <TextCard
            blok={{
              ...blok.textCard[0],
              contentClass: 'prose-lg md:prose',
            }}
          />
        </div>

        <div className="max-w-5xl py-16 mx-auto lg:py-20 md:px-8">
          <div className="flex flex-wrap items-center justify-center -ml-16">
            <div className="pl-16 mx-auto w-72 md:w-full md:max-w-23 lg:w-1/3">
              <div className="relative">
                <motion.svg
                  className="w-full h-auto"
                  width="267"
                  height="284"
                  viewBox="0 0 267 284"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <motion.path d={morph} fill={color} />
                </motion.svg>

                <div className="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
                  {testimonials.map((testimonial, i) => (
                    <React.Fragment key={`image${testimonial._uid}`}>
                      {selectedTab === i && (
                        <AnimatePresence exitBeforeEnter>
                          <motion.div
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 10, opacity: 0 }}
                          >
                            <Image blok={testimonial.image} />
                          </motion.div>
                        </AnimatePresence>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
            <div className="w-full pl-16 mt-12 text-center lg:w-2/3 lg:mt-0 lg:text-left">
              {testimonials.map((testimonial, i) => (
                <React.Fragment key={`desc${testimonial._uid}`}>
                  {selectedTab === i && (
                    <AnimatePresence exitBeforeEnter>
                      <motion.div
                        key={`${testimonial._uid}${selectedTab}`}
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 10, opacity: 0 }}
                        transition={{ ease: 'easeInOut', damping: 10, duration: 0.3 }}
                        className="mx-auto lg:mx-0"
                      >
                        <h3
                          className="font-normal text-white leading-[1.3em] font-heading text-[1.4rem] md:text-[1.8rem] md:text-h3"
                          dangerouslySetInnerHTML={{ __html: testimonial.quote }}
                        />
                        {testimonial.companyName && (
                          <div className="text-white font-extrabold text-20px leading-[1.4em] opacity-70 font-heading mt-6 tracking-[0.01em]">
                            {testimonial.companyName}
                          </div>
                        )}
                      </motion.div>
                    </AnimatePresence>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <section className={`mb-12`}>
          <div className="container">
            <div className="flex items-center justify-center mt-8 md:-ml-3">
              {testimonials.map((testimonial, i) => (
                <div
                  key={`testimonial${i}`}
                  onClick={() => setSelectedTab(i)}
                  className={`flex w-1/2 md:w-auto items-center justify-center md:pl-3 mb-4 md:mb-0 max-w-[6rem] md:max-w-[10rem] h-7 cursor-pointer transition-transform duration-200  ${
                    selectedTab === i ? 'transform -translate-y-2' : 'opacity-60'
                  } hover:opacity-100  transition-opacity duration-200 ease-in-out mx-4 md:mx-6`}
                >
                  <Image
                    blok={testimonial.image}
                    className="w-full h-full"
                    imageClassName="mx-auto filter invert w-full h-full object-contain"
                    imgStyle={{ objectFit: 'contain' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="flex justify-center">
          <Button blok={blok.button[0]} color={`white`} />
        </div>
      </div>
    </section>
  );
}
